import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiService from '../../services/api';
import { exportToCsv } from '../../utils/helpers';
import { useAppContext } from '../../context/AppContext';

const ProgramAirings = ({ programId }) => { // Accept programId as prop
  const { t, availableTimezones, userTimezoneId } = useAppContext();
  const [airings, setAirings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('fullDate');  // Default sort by date
  const [sortDirection, setSortDirection] = useState('desc'); // Default sort direction is descending
  
  // Get timezone abbreviation for display
  const currentTimezone = availableTimezones.find(tz => tz.id === userTimezoneId);
  const timezoneAbbr = currentTimezone ? (currentTimezone.abbr || currentTimezone.name) : 'UTC';
  
  // Handle column sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // If already sorting by this field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If sorting by a new field, set it and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Fetch airings data (wrapped in useCallback)
  const fetchAirings = useCallback(async () => {
    if (!programId) return; // Use programId prop

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getScheduled(programId, userTimezoneId); // Pass timezone ID
      // Backend now returns { data: [], timezone: {} }
      const data = response.data || response; // Handle both old and new response format
      setAirings(Array.isArray(data) ? data : []); // Ensure data is an array
    } catch (err) {
      console.error('Failed to fetch program airings:', err);
      setError(t('programAirings.error'));
      setAirings([]); // Set to empty array on error
    } finally {
      setIsLoading(false);
    }
  }, [programId, userTimezoneId, t]); // Add userTimezoneId and t as dependencies

  // Fetch data on mount or when programId or timezone changes
  useEffect(() => {
    fetchAirings();
  }, [fetchAirings]);

  // Effect to refresh airings when timezone changes
  useEffect(() => {
    // Only refresh if we already have data loaded (meaning we're viewing airings)
    if (programId && !isLoading) {
      console.log("Timezone changed, refreshing program airings with new timezone");
      fetchAirings();
    }
  }, [userTimezoneId]); // Only watch userTimezoneId to avoid loops

  // Format duration from PT00H31M format to 31 min
  const formatDuration = (durString) => {
    if (!durString) return 'N/A';

    const hourMatch = durString.match(/(\d+)H/);
    const minuteMatch = durString.match(/(\d+)M/);

    const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
    const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;

    if (hours === 0) {
      return `${minutes} min`;
    } else if (minutes === 0) {
      return `${hours} hr`;
    } else {
      return `${hours} hr ${minutes} min`;
    }
  };

  // Format qualifiers string to more readable format
  const formatQualifiers = (quals) => {
    if (!quals) return 'None';
    return quals.split('|').join(', ');
  };
  
  // Sort airings data based on sortField and sortDirection
  const sortedAirings = useMemo(() => {
    if (!airings || airings.length === 0) return [];
    
    return [...airings].sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      // Handle special case for date sorting
      if (sortField === 'fullDate') {
        // Convert to dates for proper comparison
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }
      
      // Compare values
      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [airings, sortField, sortDirection]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-500">
        <p>{error}</p>
        <button
          className="mt-2 px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
          onClick={fetchAirings} // Retry fetch
        >
          {t('programTitles.tryAgain')}
        </button>
      </div>
    );
  }

  if (!airings || airings.length === 0) { // Check if airings is null or empty
    return (
      <div className="p-6 text-center text-gray-500">
        <p>{t('programAirings.noAirings')}</p>
      </div>
    );
  }

  return (
    <div className="p-6 overflow-auto h-full" style={{ maxHeight: 'calc(100vh - 300px)' }}>
      <h2 className="text-xl font-semibold mb-4 text-left">{t('programAirings.title')}</h2>
      <div className="overflow-x-auto overflow-y-auto">
        <table className="w-full bg-white border border-gray-200 text-left">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('callSign')}
              >
                {t('programAirings.columns.callSign')}
                {sortField === 'callSign' && (
                  <span className="ml-1">
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programAirings.columns.channelName')}
              </th>
              <th 
                className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('prgSvcId')}
              >
                {t('programAirings.columns.prgSvcId')}
                {sortField === 'prgSvcId' && (
                  <span className="ml-1">
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programAirings.columns.city')}
              </th>
              <th 
                className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('fullDate')}
              >
                {t('programAirings.columns.date')}
                {sortField === 'fullDate' && (
                  <span className="ml-1">
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programAirings.columns.time')} ({timezoneAbbr})
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programAirings.columns.duration')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programAirings.columns.network')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programAirings.columns.type')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programAirings.columns.qualifiers')}
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                {t('programAirings.columns.tvRating')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 text-left">
            {sortedAirings.map((airing, index) => (
              <tr key={airing.eventId || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {airing.callSign || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {airing.name || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {airing.prgSvcId || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {airing.city || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {airing.fullDate || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {airing.time || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {formatDuration(airing.dur)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {airing.netSynSource || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {airing.netSynType || 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900">
                  {formatQualifiers(airing.quals)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                  {airing.tvRating || 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-between text-xs text-gray-500 px-4 pb-4">
        <div>
          {t('programAirings.showing', { count: sortedAirings.length })}
        </div>
        <div>
          <button
            className={`px-3 py-1 border border-gray-300 rounded-sm ${sortedAirings.length === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
            onClick={() => exportToCsv(sortedAirings, `${programId}_airings.csv`)}
            disabled={sortedAirings.length === 0}
          >
            {t('programAirings.export')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProgramAirings;
